import React, { useState } from 'react';
import {
  Layers,
  ShieldCheck,
  Eye,
  Sliders,
  CheckCircle2,
  XCircle,
  Maximize2,
  Info,
  Smartphone,
  Monitor
} from 'lucide-react';
import { AdSlotItem, AppView } from '../../types';
import { monetizationEngine } from '../../services/monetizationEngine';
import { AdSlot } from './AdSlot';

export const AdInventoryManager: React.FC = () => {
  const [slots, setSlots] = useState<AdSlotItem[]>(monetizationEngine.getAdInventory());
  const protectedZones = monetizationEngine.getProtectedZones();
  const [selectedSlot, setSelectedSlot] = useState<AdSlotItem | null>(slots[0] || null);

  const handleToggle = (id: string, currentEnabled: boolean) => {
    monetizationEngine.toggleAdSlot(id, !currentEnabled);
    setSlots(monetizationEngine.getAdInventory());
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold border border-blue-500/30">
          <Layers className="w-3.5 h-3.5" />
          YamooPay Ad Inventory & Placement Controller
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Global Ad Inventory & Spatial Governance</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Manage ad placements, reserve container dimensions to guarantee zero Cumulative Layout Shift (CLS), and inspect Protected Calculation Zones.
        </p>
      </div>

      {/* Grid of Slots & Detail Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Slot List */}
        <div className="lg:col-span-7 space-y-4">
          <h2 className="text-lg font-bold text-slate-900 flex items-center justify-between">
            <span>Configured Ad Inventory Slots ({slots.length})</span>
            <span className="text-xs font-semibold text-slate-500">
              {slots.filter((s) => s.enabled).length} Active
            </span>
          </h2>

          <div className="space-y-3">
            {slots.map((slot) => (
              <div
                key={slot.id}
                onClick={() => setSelectedSlot(slot)}
                className={`p-4 rounded-xl border transition-all cursor-pointer space-y-2 ${
                  selectedSlot?.id === slot.id
                    ? 'bg-blue-50/60 border-blue-300 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
                      {slot.placementKey}
                    </span>
                    <h3 className="font-bold text-sm text-slate-900">{slot.label}</h3>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(slot.id, slot.enabled);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                      slot.enabled
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                  >
                    {slot.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>

                <p className="text-xs text-slate-600">{slot.position}</p>

                <div className="flex flex-wrap items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                  <span className="font-mono">
                    {slot.reservedDimensions.width}x{slot.reservedDimensions.height}px
                  </span>
                  <span>Provider: <strong className="text-slate-700">{slot.provider}</strong></span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3 text-blue-500" /> Viewability: {slot.viewabilityRate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Slot Detail & Interactive Live Preview */}
        <div className="lg:col-span-5 space-y-6">
          {selectedSlot && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5 sticky top-6">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                    Placement Inspector
                  </span>
                  <h3 className="text-lg font-bold text-slate-900">{selectedSlot.label}</h3>
                </div>
                <span className="px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono font-bold">
                  {selectedSlot.provider}
                </span>
              </div>

              <div className="space-y-3 text-xs text-slate-700">
                <div className="flex justify-between border-b border-slate-50 pb-1.5">
                  <span className="text-slate-400">Page Type:</span>
                  <span className="font-bold text-slate-800">{selectedSlot.pageType}</span>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-1.5">
                  <span className="text-slate-400">Reserved Dimensions:</span>
                  <span className="font-mono font-bold text-slate-800">
                    {selectedSlot.reservedDimensions.width} x {selectedSlot.reservedDimensions.height} px
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-1.5">
                  <span className="text-slate-400">Policy State:</span>
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                    {selectedSlot.monetizationPolicy}
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-50 pb-1.5">
                  <span className="text-slate-400">Estimated RPM:</span>
                  <span className="font-mono font-bold text-slate-800">${selectedSlot.estimatedRpm.toFixed(2)}</span>
                </div>
              </div>

              {/* Slot Live Preview Container */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="text-xs font-bold text-slate-700 flex items-center gap-1">
                  <Maximize2 className="w-3.5 h-3.5 text-blue-600" />
                  Container Live Render (CLS Protected)
                </div>
                <AdSlot placementKey={selectedSlot.placementKey} />
              </div>
            </div>
          )}

          {/* Protected Calculation Zones Panel */}
          <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <h3 className="font-bold text-sm">Protected Calculation Zones ({protectedZones.length})</h3>
            </div>

            <div className="space-y-3 text-xs">
              {protectedZones.map((pz) => (
                <div key={pz.id} className="p-3 bg-slate-800 rounded-xl border border-slate-700 space-y-1">
                  <div className="flex items-center justify-between text-emerald-300 font-bold">
                    <span>{pz.zoneName}</span>
                    <span className="text-[10px] bg-emerald-500/20 px-2 py-0.5 rounded border border-emerald-500/30">
                      Clean
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-300">{pz.noAdInjectReason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
